import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import {FolderSummary, MinimalFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {IntentLevel} from 'file-browser/FileSelectorConstants';
import FolderDAO from 'file-browser/FolderDAO';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelectorModalClass from 'file-browser/FolderSelectorModal';
import * as RegistryUtil from 'registry/RegistryUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export interface FolderSelectorProps {
  allowCreateFolder?: boolean;
  allowNone: boolean;
  buttonSize?: ButtonSize;
  className?: string;
  enableRedesign: boolean;
  error: string;
  level: IntentLevel;
  noFolderText: string;
  readOnly: boolean;
  requireSubfolderCreatableAuth: boolean;
  showInventory: boolean;
  showNotebook: boolean;
  showRegistries: boolean;
  unsetOnFetchFail: boolean;
  value: string | null;
  // All TS callers of FolderSelector use GenericEvent as the parameter in the onChange callback but this
  // component unsets the selected folder with {target:{value:null}} which is incompatible with GenericEvent
  // so defining the type with "any" here. This event handler should be refactored to accept a more specific
  // callback anyway so prefering "any" for now instead of refactoring across all uses twice.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (e: {target: {value: any}}) => void;
  dataTestAttrs?: DataTestAttrs;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentTagSchema?: any;
  /**
   * If the folder selector modal is opened from a tooltip, its z-index may need to be raised to render
   * on top of that tooltip.
   *
   * @default 1040
   */
  selectorModalZIndex?: 1040 | 1060;
  /**
   * Set to true if the item being moved is an existing item. This checks Data Mods settings
   * to determine if movement between folders of different data mods settings is allowed.
   */
  checkExistingItemForDataModsMovement: boolean;
}

export interface FolderSelectorState {
  active: boolean;
  folders: Array<MinimalFolder> | null;
  fetchedId: string | null;
  fetchedName: string | null;
  isFetchingName: boolean;
}

export const UNKNOWN_FOLDER = 'UNKNOWN_FOLDER';
export const DEFAULT_PROJECT_FOLDER_LABEL_TEXT = 'Project folder';

// Renders a button for selecting a folder (of the logged-in user's ANY folders)
// Takes in value/onChange for the selected folder id.
export default class FolderSelector extends React.Component<FolderSelectorProps, FolderSelectorState> {
  _isMounted: boolean = false;

  static defaultProps: Partial<FolderSelectorProps> = {
    allowNone: false,
    enableRedesign: false,
    level: 'append',
    noFolderText: 'Choose folder',
    readOnly: false,
    requireSubfolderCreatableAuth: false,
    showInventory: false,
    showNotebook: false,
    showRegistries: false,
    unsetOnFetchFail: false,
    checkExistingItemForDataModsMovement: false,
  };

  state: FolderSelectorState = {
    fetchedId: null,
    fetchedName: null,
    active: false,
    folders: null,
    isFetchingName: false,
  };

  componentDidMount(): void {
    this._isMounted = true;
    // We fetch after mounting, since the fetches may be cached, which makes them synchronous.
    // The callback's _isMounted check would prevent setState even though setState before mount is fine.
    const {folderId} = RegistryUtil.parseFolderValue(this.props.value);
    this.fetchFolders();
    this.fetchName(folderId);
    FolderDAOCache.on(FolderDAOCache.FOLDER_CACHE_INVALIDATE, () => {
      this._refetchFolders();
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps: FolderSelectorProps): void {
    const {folderId} = RegistryUtil.parseFolderValue(nextProps.value);
    if (folderId !== this.state.fetchedId) {
      this.fetchName(folderId);
    }
  }

  componentDidUpdate(prevProps: FolderSelectorProps, prevState: FolderSelectorState): void {
    // If selected folder or folders have changed, re-render the modal.
    const folderChanged = prevState.folders !== this.state.folders;
    const selectedChanged = prevProps.value !== this.props.value;
    if ((folderChanged || selectedChanged) && this.state.active) {
      this.openModal();
    }
  }

  componentWillUnmount(): void {
    FolderSelectorModalClass.close();
    FolderDAOCache.off(FolderDAOCache.FOLDER_CACHE_INVALIDATE, () => {
      this._refetchFolders();
    });
    this._isMounted = false;
  }

  fetchFolders(): void {
    FolderDAO.fetchAll({level: this.props.level}).done((folders: Array<MinimalFolder>) => {
      // Deferring @setState as a workaround to the bug that React is trying to attach the
      // onClick listener to the component after it was unmounted, which was causing
      // 'Cannot read property 'firstChild' of undefined' error
      // (https://github.com/facebook/react/issues/7440). The trigger of the bug is  that we
      // are mounting and unmounting the component from a backbone view in the same stack frame,
      // which is causing issues when the React runtime applies some render changes asynchronously
      // (hence why _.defer is useful).
      _.defer(() => {
        if (this._isMounted) {
          this.setState({folders});
        }
      });
    });
  }

  _getName(): string {
    const {folderId} = RegistryUtil.parseFolderValue(this.props.value);

    const folder = _.findWhere(this.state.folders!, {api_identifier: folderId});
    if (folder != null) {
      return folder.name;
    }
    if (this.state.fetchedId === folderId) {
      return this.state.fetchedName || (this.state.isFetchingName ? 'Loading...' : '(No access)');
    }
    return 'Unknown';
  }

  fetchName(folderId: string | null): void {
    if (folderId === UNKNOWN_FOLDER) {
      return;
    }
    this.setState({fetchedId: folderId, fetchedName: null});
    if (!folderId) {
      return;
    }
    this.setState({isFetchingName: true});
    FolderDAO.fetch(folderId, {summary: '1'})
      .done((folder: FolderSummary) => {
        if (this._isMounted) {
          this.setState({fetchedName: folder.name});
        }
      })
      .fail(() => {
        if (this._isMounted) {
          if (this.props.readOnly) {
            this.setState({fetchedId: null});
          } else if (this.props.unsetOnFetchFail) {
            this.props.onChange({target: {value: null}});
          }
        }
      })
      .always(() => {
        this.setState({isFetchingName: false});
      });
  }

  addFolder = (folder: MinimalFolder): void => {
    // This is safe to do since minimal folders are a strict subset of folders.
    const index = _.sortedIndex(this.state.folders!, folder, 'name');
    this.setState({
      folders: reactUpdate(this.state.folders, {
        $splice: [[index, 0, folder]],
      }),
    });
  };

  _refetchFolders(): void {
    // If a folder has been created in a FolderSelectorModal, refetch folders in other FolderSelectorModals.
    // Note that we do not need to refetch if the folder was created in the modal since it is added directly
    // in the component (resulting in better performance).
    if (!this.state.active) {
      this.fetchFolders();
    }
  }

  closeModal = (): void => {
    this.setState({active: false});
    FolderSelectorModalClass.close();
  };

  openModal(): void {
    FolderSelectorModalClass.open({
      allowCreateFolder: this.props.allowCreateFolder,
      allowNone: this.props.allowNone,
      addFolder: this.addFolder,
      folders: this.state.folders!,
      onChange: this.props.onChange,
      onRequestClose: this.closeModal,
      initialFolderId: this.props.value,
      requireSubfolderCreatableAuth: this.props.requireSubfolderCreatableAuth,
      showInventory: this.props.showInventory,
      showNotebook: this.props.showNotebook,
      showRegistries: this.props.showRegistries,
      zIndex: this.props.selectorModalZIndex,
      isExistingItem: this.props.checkExistingItemForDataModsMovement,
    });
  }

  clickHandler = (): void => {
    this.setState({active: true});
    this.openModal();
  };

  render(): JSX.Element {
    let isReadOnly;
    let icon: BenchlingIconName | undefined;
    let text;
    if (this.state.folders == null) {
      isReadOnly = true;
      text = 'Loading...';
    } else {
      const {folderId} = RegistryUtil.parseFolderValue(this.props.value);
      isReadOnly = this.props.readOnly || this.props.onChange == null;
      icon = ModelTypeConstants.FOLDER;
      text = folderId ? this._getName() : this.props.noFolderText;
    }
    const sharedProps = {
      onClick: !isReadOnly ? this.clickHandler : undefined,
      disabled: isReadOnly,
    };
    const dataTestAttrs = this.props.dataTestAttrs ?? {component: 'FolderSelector', element: 'root'};
    const button = this.props.enableRedesign ? (
      <Button
        {...sharedProps}
        className={classNames(this.props.className, {
          folderSelector: true,
          'is-active': this.state.active,
          'fieldInput-input--error': Boolean(this.props.error),
        })}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        fullWidth={true}
        icon={icon}
        type="button"
        size={this.props.buttonSize || ButtonSize.SMALL}
        text={text}
        dataTestAttrs={dataTestAttrs}
      />
    ) : (
      <button
        {...sharedProps}
        className={classNames(this.props.className, {
          'folderSelector btn btn-af-default': true,
          active: this.state.active,
          'fieldInput-input--error': Boolean(this.props.error),
        })}
        type="button"
        {...getTestProps(dataTestAttrs)}
      >
        {icon && <BenchlingIcon className="u-faded" icon={icon} />}
        {` `}
        {text}
      </button>
    );
    return <TooltipWrapper tooltipTitle={text}>{button}</TooltipWrapper>;
  }
}
